<template>
  <section
    :class="[
      'landing-section',
      image.isFirst && 'image-first',
      image.isFullscreen && 'full-screen',
      image.isSmall && 'small-image',
    ]"
  >
    <div
      :class="[
        'landing-section__body',
        image.isFullscreen && 'full-screen',
      ]"
    >
      <div
        v-if="hasLabel"
        class="landing-section__label"
        :style="{color: textColor, 'border-color': textColor }"
      >
        Exclusive for content creators and influencers
      </div>
      <h1
        v-if="isLargeTitle"
        :class="{
          'full-screen': image.isFullscreen
        }"
        :style="{color: textColor}"
      >{{ title }}</h1>
      <h2
        v-else
        :style="{color: textColor}"
      >
        {{ title }}
      </h2>
      <p
        v-html="description"
        class="landing-section__description"
        :style="{color: textColor}"
      />
      <slot>
        <div class="landing-button__container_left">
          <TLink
            class="landing-button analytics-go-to-signup"
            :style="{'background-color': textColor,'color': buttonTextColor}"
            :to="{name: to}"
          >
            Start your page
          </TLink>
        </div>
      </slot>
    </div>
    <div
      v-if="hasVideo"
      class="landing-section__video-container"
    >
      <div class="landing-section__video-wrapper">
        <TVideoPlayer :options="videoOptions"/>
      </div>
    </div>
    <div
      v-else
      :class="[
        'landing-section__image',
        image.isFullscreen && 'fullScreen',
        image.isSmall && 'smallImage',
      ]"
    >
      <img
        loading="lazy"
        alt="image"
        :src="imageSrc"
      >
    </div>
  </section>
</template>

<script>

import TLink from '@/components/base/TLink/TLink';
import { TVideoPlayer } from '@/components/TVideoPlayer';

export default {
  name: 'LandingSection',
  components: { TVideoPlayer, TLink },
  props: {
    textColor: {
      type: String,
      required: true,
    },
    buttonTextColor: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    isLargeTitle: {
      type: Boolean,
      default: false,
    },
    image: {
      type: Object,
      required: true,
    },
    to: {
      type: String,
      required: true,
    },
    hasLabel: {
      type: Boolean,
      default: false,
    },
    video: {
      type: Object,
    },
  },
  inject: [
    'screen',
  ],
  computed: {
    imageSrc() {
      const {
        src,
        srcDesktop,
        srcMobile,
      } = this.image;

      return (this.screen.isDesktop ? srcDesktop : srcMobile) || src;
    },
    hasVideo() {
      const { video } = this;

      return video && video.src;
    },
    videoOptions() {
      const {
        hasVideo,
        video,
      } = this;

      if (!hasVideo) {
        return {};
      }

      return {
        autoplay: true,
        muted: true,
        controls: true,
        sources: [
          {
            src: video.src,
            type: 'video/mp4',
          },
        ],
        poster: video.poster,
        fluid: true,
        aspectRatio: '16:9',
      };
    },
  },
};
</script>


<style scoped lang="scss"
>
@import "~@/assets/scss/mixins";


.landing-section {
  display: flex;
  flex-direction: column;
  gap: 32px;

  @include media-lg() {
    flex-direction: row;
    gap: 80px;

    &.image-first {
      flex-direction: row-reverse;
    }

    &.full-screen {
      gap: 60px;
    }

    &.small-image {
      gap: 174px;
    }
  }

  &__label {
    display: inline-block;
    margin: 0 auto 32px auto;
    padding: 12px 17px;
    width: fit-content;
    border-width: 2px;
    border-style: solid;

    font-size: 14px;
    line-height: 24px;
    border-radius: 30px;
    text-align: center;

    @include media-lg() {
      margin: 0 0 32px 0;
      padding: 12px 20px;

      font-size: 16px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    justify-content: center;

    &.full-screen {
      @include media-lg() {
        flex: 0 0 442px;
      }
    }
  }

  &__image {
    object-fit: contain;

    @include media-sm-only() {
      display: flex;
      justify-content: center;
      height: 327px;

      &.fullScreen {
        margin-bottom: -32px;
      }
    }

    @include media-lg() {
      height: 568px;
      flex: 0 0 568px;

      &.fullScreen {
        margin: -128px 0;
        height: 703px;
        flex-basis: auto;
      }

      &.smallImage {
        height: 445px;
      }
    }

    img {
      height: 100%;
    }
  }

  &__description {
    margin-bottom: 24px;

    font-size: 16px;
    line-height: 24px;
    font-weight: 300;
    text-align: center;

    @include media-lg() {
      font-size: 20px;
      line-height: 32px;
      text-align: left;
    }

    ::v-deep span {
      font-weight: 700;
    }
  }

  &__video-container {
    display: flex;
    justify-content: center;
    width: 100%;

    @include media-lg() {
      justify-content: flex-end;
    }
  }

  &__video-wrapper {
    align-self: center;
    width: 327px;
    height: 184px;

    border-radius: 30px;
    overflow: hidden;

    @include media-lg() {
      width: 567px;
      height: 318px;
    }
  }

  &__button {
    display: inline-block;
    text-align: center;
    padding: 12px 24px;
    border-radius: 100px;
    width: fit-content;

    @include media-sm-only() {
      width: 100%;
    }
  }

  h1 {
    margin-bottom: 24px;

    font-size: 40px;
    line-height: 48px;
    font-weight: 700;
    text-align: center;

    @include media-lg() {
      margin-bottom: 48px;

      font-size: 64px;
      line-height: 64px;
      text-align: left;

      &.full-screen {
        margin-bottom: 24px;

        font-size: 51px;
      }
    }
  }

  h2 {
    margin-bottom: 24px;

    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    text-align: center;


    @include media-lg() {
      margin-bottom: 48px;

      font-size: 40px;
      line-height: 48px;
      text-align: left;
    }
  }

  form {

    + p {
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 24px;

      @include media-lg() {
        text-align: center;
      }
    }
  }

}
</style>
